(function () {
  const DETAILS_SELECTOR = '.product__accordion > details';
  const SUMMARY_SELECTOR = '.product__accordion > details > summary';
  const CONTENT_SELECTOR = ':scope > .accordion__content';
  const DURATION = 320;
  const EASING = 'cubic-bezier(0.33, 1, 0.68, 1)';

  const instances = new WeakMap();

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  class ProductAccordion {
    constructor(details) {
      this.details = details;
      this.summary = details.querySelector(':scope > summary');
      this.content = details.querySelector(CONTENT_SELECTOR);
      this.animation = null;
      this.isClosing = false;
      this.isExpanding = false;
    }

    // offsetHeight excludes margins, and .accordion__content carries a
    // bottom margin — without it the animation lands short and snaps.
    contentHeight() {
      const styles = window.getComputedStyle(this.content);
      return (
        this.content.offsetHeight +
        parseFloat(styles.marginTop) +
        parseFloat(styles.marginBottom)
      );
    }

    toggle() {
      if (this.isClosing || !this.details.open) this.open();
      else this.close();
    }

    open() {
      this.closeSiblings();

      if (prefersReducedMotion()) {
        this.setState(true);
        return;
      }

      this.details.style.overflow = 'hidden';
      this.details.style.height = this.details.offsetHeight + 'px';
      this.details.open = true;
      window.requestAnimationFrame(() => this.expand());
    }

    expand() {
      this.isExpanding = true;
      const start = this.details.offsetHeight + 'px';
      const end = this.summary.offsetHeight + this.contentHeight() + 'px';
      this.animate(start, end, true);
    }

    close() {
      if (prefersReducedMotion()) {
        this.setState(false);
        return;
      }

      this.isClosing = true;
      this.details.style.overflow = 'hidden';
      const start = this.details.offsetHeight + 'px';
      const end = this.summary.offsetHeight + 'px';
      this.animate(start, end, false);
    }

    animate(start, end, open) {
      if (this.animation) this.animation.cancel();
      this.animation = this.details.animate(
        { height: [start, end] },
        { duration: DURATION, easing: EASING }
      );
      this.animation.onfinish = () => this.finish(open);
      this.animation.oncancel = () => {
        this.isClosing = false;
        this.isExpanding = false;
      };
    }

    closeSiblings() {
      const scope = this.details.closest('.product__info-container') || document;
      scope.querySelectorAll(DETAILS_SELECTOR).forEach((other) => {
        if (other === this.details || !other.open) return;
        const instance = register(other);
        if (instance && !instance.isClosing) instance.close();
      });
    }

    finish(open) {
      this.animation = null;
      this.isClosing = false;
      this.isExpanding = false;
      this.details.style.height = '';
      this.details.style.overflow = '';
      this.setState(open);
    }

    // global.js only syncs aria-expanded on direct clicks, so a row closed
    // programmatically would otherwise keep announcing itself as expanded.
    setState(open) {
      this.details.open = open;
      if (this.summary) this.summary.setAttribute('aria-expanded', String(open));
    }
  }

  function register(details) {
    let instance = instances.get(details);
    if (!instance) {
      if (!details.querySelector(CONTENT_SELECTOR)) return null;
      instance = new ProductAccordion(details);
      instances.set(details, instance);
    }
    return instance;
  }

  // Delegated so section re-renders (variant change, theme editor) need no re-init.
  document.addEventListener('click', (event) => {
    const summary = event.target.closest(SUMMARY_SELECTOR);
    if (!summary) return;

    const instance = register(summary.parentElement);
    if (!instance) return;

    event.preventDefault();
    instance.toggle();
  });
})();

/* Keeps the swatch option name in the legend in sync with the checked swatch.
   Sense's VariantRadios only re-renders price/sku/inventory, so the picker
   markup (and therefore the legend) persists across a variant change. */
(() => {
  const INPUT_SELECTOR = '.product-form__input--swatch input[type="radio"]';

  document.addEventListener('change', (event) => {
    const input = event.target;
    if (!input.matches || !input.matches(INPUT_SELECTOR)) return;

    const output = input.closest('fieldset')?.querySelector('[data-selected-option-value]');
    if (output) output.textContent = input.value;
  });
})();

/* Mobile sticky add-to-cart bar. The button submits the real product form via
   its form attribute, so the only work here is showing the bar once the inline
   button scrolls out of view and mirroring that button's state and the price,
   both of which Sense re-renders on a variant change. */
class StickyAtc extends HTMLElement {
  connectedCallback() {
    this.sectionId = this.dataset.sectionId;
    this.submit = this.querySelector('[data-sticky-submit]');
    this.submitText = this.querySelector('[data-sticky-submit-text]');
    this.priceOutput = this.querySelector('[data-sticky-price]');

    this.inlineSubmit = document.getElementById(`ProductSubmitButton-${this.sectionId}`);
    this.inlinePrice = document.getElementById(`price-${this.sectionId}`);
    if (!this.inlineSubmit) return;

    this.mobile = window.matchMedia('(max-width: 749px)');
    this.mobile.addEventListener('change', () => this.toggle(this.offscreen));

    this.observer = new IntersectionObserver(
      (entries) => {
        this.offscreen = !entries[0].isIntersecting;
        this.toggle(this.offscreen);
      },
      { threshold: 0 }
    );
    this.observer.observe(this.inlineSubmit);

    this.syncSubmit();
    this.syncPrice();

    this.submitWatcher = new MutationObserver(() => this.syncSubmit());
    this.submitWatcher.observe(this.inlineSubmit, {
      attributes: true,
      childList: true,
      subtree: true,
      characterData: true,
    });

    if (this.inlinePrice) {
      this.priceWatcher = new MutationObserver(() => this.syncPrice());
      this.priceWatcher.observe(this.inlinePrice, { childList: true, subtree: true, characterData: true });
    }
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.submitWatcher?.disconnect();
    this.priceWatcher?.disconnect();
    document.body.classList.remove('baa-sticky-atc-visible');
  }

  toggle(show) {
    const visible = Boolean(show) && this.mobile.matches;
    this.classList.toggle('is-visible', visible);
    document.body.classList.toggle('baa-sticky-atc-visible', visible);
  }

  syncSubmit() {
    if (!this.submit) return;

    const disabled =
      this.inlineSubmit.disabled || this.inlineSubmit.getAttribute('aria-disabled') === 'true';
    this.submit.disabled = disabled;

    const label = this.inlineSubmit.querySelector('span');
    if (label && this.submitText) this.submitText.textContent = label.textContent.trim();
  }

  syncPrice() {
    if (!this.priceOutput || !this.inlinePrice) return;

    const sale = this.inlinePrice.querySelector('.price-item--sale');
    const regular = this.inlinePrice.querySelector('.price-item--regular');
    const onSale = this.inlinePrice.querySelector('.price--on-sale');
    const source = onSale && sale ? sale : regular;
    if (source) this.priceOutput.textContent = source.textContent.trim();
  }
}

if (!customElements.get('sticky-atc')) customElements.define('sticky-atc', StickyAtc);

/* Product descriptions come out of Google Sheets as bullet characters separated
   by <br>, wrapped in whatever markup the paste dragged along. Rebuild those
   runs as real lists so the bullets can hang, working on nodes rather than
   strings so bold, italics and links survive the move. */
(() => {
  const BULLET = /^[\s ]*[•▪●·]+[\s \t]*/;

  const firstTextNode = (node) => {
    if (node.nodeType === Node.TEXT_NODE) return node.nodeValue.trim() ? node : null;
    for (const child of node.childNodes) {
      const found = firstTextNode(child);
      if (found) return found;
    }
    return null;
  };

  const textOf = (nodes) => nodes.map((node) => node.textContent).join('').trim();

  const convert = (host) => {
    const lines = [[]];
    const breaks = [];

    host.childNodes.forEach((node) => {
      if (node.nodeName === 'BR') {
        breaks.push(node);
        lines.push([]);
      } else {
        lines[lines.length - 1].push(node);
      }
    });

    const first = lines.findIndex((line) => BULLET.test(textOf(line)));
    if (first === -1 || lines.length < 2) return;

    const list = document.createElement('ul');
    let item = null;

    lines.slice(first).forEach((line) => {
      if (!textOf(line)) return;

      if (BULLET.test(textOf(line))) {
        item = document.createElement('li');
        list.appendChild(item);

        for (const node of line) {
          const text = firstTextNode(node);
          if (text) {
            text.nodeValue = text.nodeValue.replace(BULLET, '');
            break;
          }
        }
      } else if (item) {
        // A break inside a bullet, not a new one. Keep it on the same item.
        item.appendChild(document.createTextNode(' '));
      }

      if (item) line.forEach((node) => item.appendChild(node));
    });

    if (!list.children.length) return;

    breaks.slice(Math.max(0, first - 1)).forEach((node) => node.remove());
    host.parentNode.insertBefore(list, host.nextSibling);

    if (!host.textContent.trim() && !host.querySelector('img')) {
      const parent = host.parentElement;
      host.remove();

      // A wrapper left holding nothing but the list it used to contain.
      if (parent && parent.tagName === 'P' && !parent.textContent.trim() && !parent.children.length) {
        parent.remove();
      }
    }
  };

  const run = (root) => {
    root.querySelectorAll('.product__description').forEach((description) => {
      if (description.querySelector('ul, ol')) return;

      const hosts = [description, ...description.querySelectorAll('*')].filter(
        (element) => element.querySelector(':scope > br') && /[•▪●·]/.test(element.textContent)
      );

      hosts.forEach((host) => {
        if (host.isConnected) convert(host);
      });
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => run(document));
  } else {
    run(document);
  }
})();

/* Real color swatches for the Appstle fixed-pricing bundle widget.

   Appstle renders each bundle line's shade picker as a plain-text fake dropdown
   (a div[role=button] trigger over div[role=option] rows) and exposes no swatch
   data, so their own CSS settings cannot colour it. The widget is React but it
   renders into the light DOM, so instead we hide its picker, draw the product's
   real Shopify swatches in its place, and let a swatch click drive the hidden
   picker. Appstle stays the source of truth for price, availability and
   add-to-bundle; this is only a skin.

   Colors come from /products/<handle>?view=swatch-json (see
   templates/product.swatch-json.liquid) because /products/x.js omits swatches,
   and because shade names repeat across products with different colors. The
   handle comes off the widget's React props. Every failure path — renamed prop,
   missing template, a shade with no swatch, an option node that has moved —
   leaves the row's normal dropdown in place rather than breaking selection. */
(() => {
  const ROW = '.ab-fixed-pricing-child-row';
  const SELECT = '.ab-fixed-pricing-variant-select';
  const TRIGGER = '.ab-fixed-pricing-variant-select-trigger';
  const LIST = '.ab-fixed-pricing-variant-select-list';
  const OPTION = '.ab-fixed-pricing-variant-select-option';
  const VALUE = '.ab-fixed-pricing-variant-select-value';
  const ENHANCED = 'baa-bundle-swatches-on';
  const OPEN_TIMEOUT = 600;

  const products = new Map();

  const swatchesFor = (handle) => {
    if (!products.has(handle)) {
      products.set(
        handle,
        fetch(`/products/${encodeURIComponent(handle)}?view=swatch-json`)
          .then((response) => (response.ok ? response.json() : null))
          .catch(() => null)
      );
    }

    return products.get(handle);
  };

  /* Appstle hands each row the product's /products/x.js payload as a prop. */
  const handleFor = (row) => {
    const fiberKey = Object.keys(row).find((key) => key.startsWith('__reactFiber'));
    let fiber = fiberKey ? row[fiberKey] : null;

    for (let depth = 0; fiber && depth < 8; depth += 1) {
      const product = fiber.memoizedProps?.childProduct;
      if (product?.handle) return product.handle;
      fiber = fiber.return;
    }

    return null;
  };

  const selectedName = (select) =>
    (select.querySelector(VALUE) || select.querySelector(TRIGGER))?.textContent.trim() || '';

  /* The option rows only exist while the list is open, and the open is a React
     state change, so poll for them rather than assuming they land in this tick. */
  const whenOpen = (select) =>
    new Promise((resolve) => {
      const started = performance.now();

      const poll = () => {
        const options = select.querySelectorAll(OPTION);
        if (options.length) return resolve([...options]);
        if (performance.now() - started > OPEN_TIMEOUT) return resolve([]);
        setTimeout(poll, 16);
      };

      poll();
    });

  const choose = async (row, name) => {
    const select = row.querySelector(SELECT);
    const trigger = select?.querySelector(TRIGGER);
    if (!trigger) return;

    if (!select.querySelector(LIST)) trigger.click();

    const option = (await whenOpen(select)).find((node) => node.textContent.trim() === name);

    if (option) {
      option.click();
      return;
    }

    /* The picker moved under us: close it, hand the row back to Appstle. */
    if (select.querySelector(LIST)) trigger.click();
    row.classList.remove(ENHANCED);
    row.querySelector('.baa-bundle-swatches')?.remove();
    delete row.dataset.baaSwatches;
  };

  const sync = (row) => {
    const select = row.querySelector(SELECT);
    const group = row.querySelector('.baa-bundle-swatches');
    if (!select || !group) return;

    const current = selectedName(select);

    group.querySelectorAll('.baa-bundle-swatch').forEach((button) => {
      const checked = button.dataset.value === current;
      button.setAttribute('aria-checked', String(checked));
      button.tabIndex = checked ? 0 : -1;
    });
  };

  /* No selected-shade caption: the app already prints the shade name beside the
     line price, so the swatches carry their names for screen readers only. */
  const render = (row, option) => {
    const select = row.querySelector(SELECT);

    const group = document.createElement('div');
    group.className = 'baa-bundle-swatches';
    group.setAttribute('role', 'radiogroup');
    group.setAttribute('aria-label', option.name);

    option.values.forEach((value) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'baa-bundle-swatch';
      if (!value.available) button.classList.add('baa-bundle-swatch--unavailable');
      button.dataset.value = value.name;
      button.setAttribute('role', 'radio');
      button.setAttribute('aria-checked', 'false');
      /* label is the presentable shade name ("Rich Deep"); name is the raw
         option value ("rich-deep") that choose() matches Appstle's option on. */
      button.title = value.label || value.name;
      button.tabIndex = -1;

      const swatch = document.createElement('span');
      swatch.className = 'swatch swatch--circle';
      swatch.style.setProperty(
        '--swatch--background',
        value.image ? `url(${value.image})` : value.color
      );

      const name = document.createElement('span');
      name.className = 'visually-hidden';
      name.textContent = value.label || value.name;

      button.append(swatch, name);
      group.append(button);
    });

    group.addEventListener('click', (event) => {
      const button = event.target.closest('.baa-bundle-swatch');
      if (!button || button.dataset.value === selectedName(select)) return;

      event.preventDefault();
      choose(row, button.dataset.value);
    });

    /* Arrow keys move through a radiogroup, so mirror that on the swatches. */
    group.addEventListener('keydown', (event) => {
      if (!/^Arrow(Left|Right|Up|Down)$/.test(event.key)) return;

      const buttons = [...group.querySelectorAll('.baa-bundle-swatch')];
      const from = buttons.indexOf(event.target.closest('.baa-bundle-swatch'));
      if (from < 0) return;

      event.preventDefault();
      const step = /Left|Up/.test(event.key) ? -1 : 1;
      const next = buttons[(from + step + buttons.length) % buttons.length];
      next.focus();
      choose(row, next.dataset.value);
    });

    select.insertAdjacentElement('afterend', group);
    row.classList.add(ENHANCED);
    sync(row);
  };

  const enhance = async (row) => {
    if (row.dataset.baaSwatches || !row.querySelector(TRIGGER)) return;

    const handle = handleFor(row);
    if (!handle) return;

    row.dataset.baaSwatches = 'pending';

    const product = await swatchesFor(handle);
    const option = product?.options?.length === 1 ? product.options[0] : null;
    const usable = option?.values.length && option.values.every((value) => value.color || value.image);

    if (!usable || !row.isConnected || row.querySelector('.baa-bundle-swatches')) {
      row.dataset.baaSwatches = 'skipped';
      return;
    }

    row.dataset.baaSwatches = handle;
    render(row, option);
  };

  const run = () => {
    document.querySelectorAll(ROW).forEach((row) => {
      if (row.classList.contains(ENHANCED)) sync(row);
      else enhance(row);
    });
  };

  /* The widget mounts late and re-renders on every add, so watch for it — but
     bail on the first cheap check, since this file loads on every page. The
     coalescing timer is a setTimeout, not a rAF: a page opened into a background
     tab never gets a frame, and the swatches should be there when it is
     foregrounded, not built on first paint. */
  let queued = false;

  const schedule = () => {
    if (queued || !document.querySelector(ROW)) return;
    queued = true;
    setTimeout(() => {
      queued = false;
      run();
    }, 0);
  };

  new MutationObserver(schedule).observe(document.documentElement, {
    childList: true,
    subtree: true,
  });

  schedule();
})();

/* Deferred assembly for the Appstle fixed-pricing bundle.

   Out of the box the shopper adds each line to the bundle by hand, watches a
   progress bar count to 5/5, and only then can check out. Worse, the app
   corrupts its own state if a shade is changed AFTER that line was added: the
   line silently reverts to "Add to bundle" while its old variant stays in the
   bundle, the counter still reads 5/5, checkout stays enabled, and the cart
   payload ships the shade the shopper moved away from. Verified against the
   app's own dropdown with our swatches removed, so it is theirs, not ours.

   So the lines are never added while the shopper browses — which is the one
   state where changing a shade is always safe — and everything that exposes
   that flow (header, progress bar, per-line add buttons, quantity steppers and
   the app's own checkout button) is hidden. A clone of their checkout button
   takes its place: on click it adds all five lines from the shades showing at
   that moment, waits for the app to agree the bundle is full, then clicks the
   real button so the app still owns the cart write.

   Never adding a line also means the stepper never renders, which is what stops
   a shopper putting three of one product into a five-product bundle.

   Any failure — a line that will not add, a checkout button that never enables —
   unhides the app's own UI so the shopper can complete the bundle by hand. */
(() => {
  const BUNDLE = '.ab-fixed-pricing-bundle';
  const ROW = '.ab-fixed-pricing-child-row';
  const ADD = '.ab-fixed-pricing-child-row-add-button';
  const QTY = '.ab-fixed-pricing-stepper-value';
  const CTA = '.ab-fixed-pricing-price-footer-checkout-button';
  const LABEL = 'span';
  const DEFERRED = 'baa-bundle-deferred';
  const STEP_TIMEOUT = 4000;

  const busy = new WeakSet();

  const until = (test, timeout) =>
    new Promise((resolve) => {
      const started = performance.now();

      const poll = () => {
        if (test()) return resolve(true);
        if (performance.now() - started > timeout) return resolve(false);
        setTimeout(poll, 32);
      };

      poll();
    });

  const added = (row) => !!row.querySelector(QTY);

  const cartRoot = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  const cartCount = async () => {
    try {
      const res = await fetch(`${cartRoot()}cart.js`, { headers: { Accept: 'application/json' } });
      if (!res.ok) return null;
      return (await res.json()).item_count;
    } catch (e) {
      return null;
    }
  };

  /* Repaint the header count bubble from a fresh render of the cart-icon-bubble
     section — the same section the theme's own cart JS re-renders. The bundle is
     added through the app's cart write, which never runs product-form.js, so
     nothing else updates the header dot. */
  const paintCartBubble = async () => {
    try {
      const res = await fetch(`${cartRoot()}?sections=cart-icon-bubble`);
      if (!res.ok) return;
      const html = (await res.json())['cart-icon-bubble'];
      /* The header duplicates its markup for desktop and mobile, so #cart-icon-bubble
         appears twice; getElementById would only reach the first (desktop) one, which
         is why the mobile bubble never refreshed. Update every instance. */
      const targets = document.querySelectorAll('#cart-icon-bubble');
      if (!html || !targets.length) return;
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const markup = (doc.querySelector('.shopify-section') || doc.body).innerHTML;
      targets.forEach((el) => {
        el.innerHTML = markup;
      });
    } catch (e) {
      /* leave the existing bubble rather than blank it */
    }
  };

  /* Poll the cart until the app's add lands (item_count moves), then repaint;
     repaint on timeout too, so a slow write still resolves. */
  const syncCartBubble = async (before) => {
    const deadline = performance.now() + 5000;
    while (performance.now() < deadline) {
      const now = await cartCount();
      if (now != null && now !== before) break;
      await new Promise((r) => setTimeout(r, 200));
    }
    await paintCartBubble();
  };

  /* Normal (non-bundle) adds go through product-form.js, which publishes cartUpdate
     but then repaints only the first #cart-icon-bubble via getElementById — so the
     mobile bubble is left stale. Repaint every instance whenever the cart changes. */
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
    subscribe(PUB_SUB_EVENTS.cartUpdate, () => paintCartBubble());
  }

  /* Hand the bundle back to the app, with every line the shopper had already
     picked left exactly as it is. The flag is what makes this stick: without it
     the observer below sees our own teardown, reinstalls, and re-hides the add
     buttons the shopper now needs. */
  const surrender = (bundle, button, label, text) => {
    bundle.dataset.baaDeferred = 'off';
    bundle.classList.remove(DEFERRED);
    button.remove();
    if (label) label.textContent = text;
  };

  const assemble = async (bundle, button) => {
    if (busy.has(bundle)) return;
    busy.add(bundle);

    const before = await cartCount();
    const label = button.querySelector(LABEL);
    const text = label ? label.textContent : '';
    if (label) label.textContent = 'Adding…';
    button.setAttribute('aria-busy', 'true');

    try {
      for (const row of bundle.querySelectorAll(ROW)) {
        if (added(row)) continue;

        const add = row.querySelector(ADD);
        if (!add) return surrender(bundle, button, label, text);

        add.click();
        if (!(await until(() => added(row), STEP_TIMEOUT))) {
          return surrender(bundle, button, label, text);
        }
      }

      const native = bundle.querySelector(`${CTA}:not([data-baa-cta])`);
      const ready = await until(
        () => native && native.getAttribute('aria-disabled') === 'false',
        STEP_TIMEOUT
      );

      if (!ready) return surrender(bundle, button, label, text);

      native.click();
      if (label) label.textContent = text;
      window.baaBundleAddedNotice?.(button);
      syncCartBubble(before);
    } finally {
      button.removeAttribute('aria-busy');
      busy.delete(bundle);
    }
  };

  const install = (bundle) => {
    if (bundle.dataset.baaDeferred === 'off') return;
    if (bundle.querySelector('[data-baa-cta]')) return;

    const native = bundle.querySelector(CTA);
    if (!native || !bundle.querySelector(ROW)) return;

    /* Built with the theme's own button classes rather than cloned from the
       app's, so it is the product page's add-to-cart button in every respect —
       height, type, casing, hover — with no styling duplicated here. The label
       text still comes from the app so its setting keeps driving the wording. */
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'product-form__submit button button--full-width button--primary';
    button.setAttribute('data-baa-cta', '');

    const label = document.createElement('span');
    label.textContent = native.textContent.trim() || 'Add to cart';
    button.append(label);

    button.addEventListener('click', () => assemble(bundle, button));

    /* Insert before hiding, so the footer is never left without a button. */
    native.insertAdjacentElement('afterend', button);
    bundle.classList.add(DEFERRED);
  };

  let queued = false;

  const schedule = () => {
    if (queued || !document.querySelector(BUNDLE)) return;
    queued = true;
    setTimeout(() => {
      queued = false;
      document.querySelectorAll(BUNDLE).forEach(install);
    }, 0);
  };

  new MutationObserver(schedule).observe(document.documentElement, {
    childList: true,
    subtree: true,
  });

  schedule();
})();

/* Moves the bundle's discount framing down to the total.

   The app's hero — parent title, thumbnail, price and "Bundle and save 10%" —
   repeats what the product page already shows, so it is hidden in
   theme-custom.css. The savings framing is worth keeping though, so the
   compare-at price and the savings note are copied down beside the total that
   sits next to the checkout button.

   Copies rather than the nodes themselves: React still owns the hero, and
   moving its children out from under it invites a removeChild crash on the next
   render. The text is re-read on each pass so a price change still lands. */
(() => {
  const BUNDLE = '.ab-fixed-pricing-bundle';
  const GROUP = '.ab-fixed-pricing-price-footer-price-group';
  const PARTS = [
    ['.ab-fixed-pricing-parent-hero-compare-at', 'compare'],
    ['.ab-fixed-pricing-parent-hero-eyebrow', 'note'],
  ];

  const TOTAL = '.ab-fixed-pricing-price-footer-total';
  const ORIGINAL = '.ab-fixed-pricing-price-footer-original';
  const LINE_PRICE = '.ab-fixed-pricing-child-row-price';
  const SCOPE = '.product-the-magen-method';

  const parseMoney = (value) =>
    parseFloat((String(value).match(/[\d.,]+/) || ['0'])[0].replace(/,/g, '')) || 0;

  /* Write an amount the way the bundle's own total is written — same currency
     symbol and thousands grouping, and cents only if the total shows them. */
  const formatLike = (amount, sample) => {
    const symbol = (sample.match(/^[^\d-]+/) || [''])[0];
    const cents = /[.,]\d{2}\s*$/.test(sample);
    const fixed = cents ? amount.toFixed(2) : String(Math.round(amount));
    const [whole, part] = fixed.split('.');
    const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return symbol + grouped + (part ? '.' + part : '');
  };

  /* Some bundles (the short one) never get the app's own strikethrough "was"
     price, even when the lines total more than the bundle — so the shopper sees
     no saving where the other bundles show one. Synthesise it from the sum of
     the line prices so every bundle in the product shows the same "full price ·
     you pay" framing. Scoped to the Magen product, and skipped whenever the app
     rendered its own original (its element carries no data-baa-price, so ours is
     not mistaken for it on the next pass). */
  const fallbackOriginal = (bundle, group) => {
    let fallback = group.querySelector('[data-baa-price="original"]');
    const total = group.querySelector(TOTAL);
    const wants =
      bundle.closest(SCOPE) && total && !bundle.querySelector(`${ORIGINAL}:not([data-baa-price])`);

    if (!wants) {
      if (fallback) fallback.remove();
      return;
    }

    const sum = [...bundle.querySelectorAll(LINE_PRICE)].reduce(
      (acc, node) => acc + parseMoney(node.textContent),
      0
    );

    if (sum <= parseMoney(total.textContent) + 0.005) {
      if (fallback) fallback.remove();
      return;
    }

    const text = formatLike(sum, total.textContent.trim());

    if (!fallback) {
      fallback = document.createElement('div');
      fallback.setAttribute('data-baa-price', 'original');
      total.insertAdjacentElement('afterend', fallback);
    }

    if (fallback.textContent.trim() !== text) fallback.textContent = text;
  };

  const sync = (bundle) => {
    const group = bundle.querySelector(GROUP);
    if (!group) return;

    PARTS.forEach(([selector, name]) => {
      const source = bundle.querySelector(selector);
      if (!source) return;

      const text = source.textContent.trim();
      if (!text) return;

      let copy = group.querySelector(`[data-baa-price="${name}"]`);

      if (!copy) {
        copy = source.cloneNode(true);
        copy.setAttribute('data-baa-price', name);
        group.append(copy);
      }

      /* Guarded: this runs off a MutationObserver, and an identical write is
         still a mutation. */
      if (copy.textContent.trim() !== text) copy.textContent = text;
    });

    fallbackOriginal(bundle, group);
  };

  let queued = false;

  const schedule = () => {
    if (queued || !document.querySelector(BUNDLE)) return;
    queued = true;
    setTimeout(() => {
      queued = false;
      document.querySelectorAll(BUNDLE).forEach(sync);
    }, 0);
  };

  new MutationObserver(schedule).observe(document.documentElement, {
    childList: true,
    subtree: true,
  });

  schedule();
})();

/* Rewrites the bundle's cart line properties on their way out.

   Appstle labels the shopper-visible property "products" and formats it as
   "1x Peptide Lip Shine SPF 30 - Pink", which is what the cart and the order
   confirmation show. There is no setting for either, so the add-to-cart request
   is intercepted and the property renamed and reformatted:

     products: 1x Peptide Lip Shine SPF 30 - Pink
     Included in Bundle: 1x Peptide Lip Shine SPF 30 (Pink)

   Only Appstle's own bundle payloads are touched — the rewrite requires both
   the "products" property and their `_appstle-bb-id` alongside it, so ordinary
   product adds pass through untouched. Their functional properties, the ones
   carrying the variant ids the bundle is actually built from, are never
   altered; only the display string is. Key order is preserved so the property
   keeps its position in the cart. */
(() => {
  const CART_WRITE = /\/cart\/(add|update|change)(\.js)?$/;
  const SOURCE = 'products';
  const TARGET = 'Included in Bundle';
  const BUNDLE_KEY = '_appstle-bb-id';

  /* "1x Name - Shade, 1x Name - Shade" → "1x Name (Shade), 1x Name (Shade)".
     Split on the comma that precedes a quantity so shade names containing a
     comma survive, and cut each entry at its LAST " - " so product names
     containing a dash do too. */
  const reformat = (value) =>
    String(value)
      .split(/,\s*(?=\d+x\s)/)
      .map((entry) => {
        const trimmed = entry.trim();
        const cut = trimmed.lastIndexOf(' - ');
        if (cut < 0) return trimmed;
        return `${trimmed.slice(0, cut).trim()} (${trimmed.slice(cut + 3).trim()})`;
      })
      .join(', ');

  const rewriteItem = (item) => {
    const properties = item && item.properties;
    if (!properties || typeof properties !== 'object') return false;
    if (!(SOURCE in properties) || !(BUNDLE_KEY in properties)) return false;

    const rebuilt = {};
    Object.keys(properties).forEach((key) => {
      if (key === SOURCE) rebuilt[TARGET] = reformat(properties[key]);
      else rebuilt[key] = properties[key];
    });

    item.properties = rebuilt;
    return true;
  };

  const rewriteBody = (raw) => {
    if (typeof raw !== 'string' || raw.indexOf(SOURCE) < 0) return null;

    let data;
    try {
      data = JSON.parse(raw);
    } catch (error) {
      return null;
    }

    const items = Array.isArray(data.items) ? data.items : [data];
    const changed = items.map(rewriteItem).some(Boolean);

    return changed ? JSON.stringify(data) : null;
  };

  const originalFetch = window.fetch;

  window.fetch = function (input, init) {
    const url = String((input && input.url) || input || '').split('?')[0];

    if (CART_WRITE.test(url) && init && typeof init.body === 'string') {
      const body = rewriteBody(init.body);
      if (body) return originalFetch.call(this, input, { ...init, body });
    }

    return originalFetch.apply(this, arguments);
  };

  /* Same treatment for XHR, in case the app ever takes that path. */
  const open = XMLHttpRequest.prototype.open;
  const send = XMLHttpRequest.prototype.send;

  XMLHttpRequest.prototype.open = function (method, url) {
    this.__baaCartWrite = CART_WRITE.test(String(url || '').split('?')[0]);
    return open.apply(this, arguments);
  };

  XMLHttpRequest.prototype.send = function (body) {
    if (this.__baaCartWrite) {
      const next = rewriteBody(body);
      if (next) return send.call(this, next);
    }

    return send.apply(this, arguments);
  };
})();

/* Stops the bundle line titles opening Appstle's single-product image lightbox.

   Each line title is a role=button that pops the product into a modal on click
   or Enter. A modal over one line of a fixed bundle only interrupts the flow,
   and the lines are not individually purchasable, so the activation is blocked.
   Capture phase at the document so it runs before Appstle's own React handler,
   delegated so it needs no re-install across the widget's re-renders, and scoped
   to the Magen product. theme-custom.css drops the clickable cursor to match. */
(() => {
  const TITLE = '.ab-fixed-pricing-child-row-title';
  const SCOPE = '.product-the-magen-method';
  const KEYS = ['Enter', ' ', 'Spacebar'];

  const block = (event) => {
    const title = event.target.closest && event.target.closest(TITLE);
    if (!title || !title.closest(SCOPE)) return;
    if (event.type === 'keydown' && !KEYS.includes(event.key)) return;

    event.stopImmediatePropagation();
    event.preventDefault();
  };

  document.addEventListener('click', block, true);
  document.addEventListener('keydown', block, true);
})();

/* The "Added bundle to the cart" confirmation, shown above the add-to-cart button.

   Appstle's own confirmation is a Sonner toast whose fixed container is trapped by
   .product__info-wrapper's scroll-in transform, so it lands below the button over
   the shipping/return rows; and Sonner rewrites the container's transform every
   frame, so it cannot be reliably nudged. Instead that toast is hidden in
   theme-custom.css and this shows a matching confirmation as a flow sibling just
   above the CTA button — no positioning math, nothing to fight. Called from the
   deferred assembler once the bundle is actually in the cart. Magen product only,
   since it hangs off the deferred [data-baa-cta] button. */
window.baaBundleAddedNotice = (button) => {
  const parent = button && button.parentElement;
  if (!parent) return;

  let note = parent.querySelector(':scope > .baa-bundle-added');
  if (!note) {
    note = document.createElement('div');
    note.className = 'baa-bundle-added';
    note.setAttribute('role', 'status');
    note.innerHTML =
      '<span class="baa-bundle-added__icon" aria-hidden="true"></span>' +
      '<span class="baa-bundle-added__text">Added bundle to the cart</span>' +
      '<button type="button" class="baa-bundle-added__close" aria-label="Dismiss">&times;</button>';
    button.insertAdjacentElement('beforebegin', note);
    note.querySelector('.baa-bundle-added__close').addEventListener('click', () => {
      clearTimeout(note.__baaTimer);
      note.classList.remove('is-visible');
    });
  }

  clearTimeout(note.__baaTimer);
  note.classList.remove('is-visible');
  void note.offsetWidth; // restart the entrance transition on a repeat add
  note.classList.add('is-visible');
  note.__baaTimer = setTimeout(() => note.classList.remove('is-visible'), 4000);
};

/* Header navigation — the desktop "Shop" mega menu and the mobile hamburger panel.

   Both used to be a div/img/a#-with-inline-onclick pair driven from an inline
   script in the theme layout, so neither was focusable, neither fired on Enter
   or Space, and there was no aria-expanded, no Escape, and no focus handling.
   The mobile menu was unopenable by keyboard outright. The triggers in the
   header section are real <button aria-expanded aria-controls> now and this is
   their controller.

   Both panels are full-width fixed overlays that cover the page, so both trap
   Tab and both close on Escape with focus returned to the trigger. The mobile
   panel additionally carries role="dialog" aria-modal="true".

   The two panels hide differently and that is deliberate: the mega menu toggles
   .is-open (visibility + transform, styled in the custom stylesheet), while the
   mobile panel keeps the display/left toggling the old inline script used,
   because the inline style="display:none" on #mobilenav is what hides it at
   load and a class could not override it. */
(function () {
  const FOCUSABLE = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
  ].join(',');

  function createDisclosure(config) {
    const trigger = document.getElementById(config.triggerId);
    const panel = document.getElementById(config.panelId);
    if (!trigger || !panel) return null;

    let isOpen = false;

    // getClientRects() rather than offsetParent: every one of these panels is
    // position:fixed, which makes offsetParent null even when fully visible.
    function focusables() {
      return Array.prototype.filter.call(
        panel.querySelectorAll(FOCUSABLE),
        (el) => el.getClientRects().length > 0
      );
    }

    // moveFocus is false only for hover opens: revealing the panel on hover
    // should not pull keyboard focus into it or scroll the page. Click and
    // keyboard activation still move focus in, as before.
    function open(moveFocus) {
      if (isOpen) return;
      isOpen = true;
      config.show(panel);
      trigger.setAttribute('aria-expanded', 'true');
      if (moveFocus === false) return;
      const first = focusables()[0];
      if (first) first.focus();
    }

    function close(returnFocus) {
      if (!isOpen) return;
      isOpen = false;
      config.hide(panel);
      trigger.setAttribute('aria-expanded', 'false');
      if (returnFocus) trigger.focus();
    }

    trigger.addEventListener('click', (event) => {
      event.preventDefault();
      isOpen ? close(true) : open(true);
    });

    const closeButton = config.closeId && document.getElementById(config.closeId);
    if (closeButton) {
      closeButton.addEventListener('click', () => close(true));
    }

    document.addEventListener('keydown', (event) => {
      if (!isOpen) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        close(true);
        return;
      }

      if (event.key !== 'Tab') return;

      const items = focusables();
      if (!items.length) {
        event.preventDefault();
        trigger.focus();
        return;
      }

      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement;

      if (!panel.contains(active)) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
      } else if (event.shiftKey && active === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    });

    if (config.closeOnOutsideClick) {
      // No focus return here — the pointer is already somewhere else, and
      // yanking focus back to the trigger on a stray click is disorienting.
      document.addEventListener('click', (event) => {
        if (!isOpen) return;
        if (panel.contains(event.target) || trigger.contains(event.target)) return;
        close(false);
      });
    }

    if (config.hoverToOpen) {
      // Desktop pointers only. On touch, (hover: hover) is false, so nothing
      // binds and a tap falls through to the click handler above unchanged —
      // the panel sits flush under the nav (margin-top: --baa-header-height),
      // so there is no dead gap to cross between the trigger and the panel.
      const hoverable = window.matchMedia('(hover: hover) and (pointer: fine)');
      let closeTimer = null;

      const cancelClose = () => {
        if (closeTimer) {
          clearTimeout(closeTimer);
          closeTimer = null;
        }
      };

      // Short delay so moving the pointer between the trigger and the panel
      // doesn't snap the menu shut. Don't close if a keyboard user has tabbed
      // inside since, and never return focus on a hover close — the pointer is
      // already elsewhere.
      const scheduleClose = () => {
        cancelClose();
        closeTimer = setTimeout(() => {
          closeTimer = null;
          if (panel.contains(document.activeElement)) return;
          close(false);
        }, 180);
      };

      const onEnter = () => {
        if (!hoverable.matches) return;
        cancelClose();
        open(false);
      };
      const onLeave = () => {
        if (!hoverable.matches) return;
        scheduleClose();
      };

      trigger.addEventListener('mouseenter', onEnter);
      trigger.addEventListener('mouseleave', onLeave);
      panel.addEventListener('mouseenter', onEnter);
      panel.addEventListener('mouseleave', onLeave);
    }

    return { open, close };
  }

  createDisclosure({
    triggerId: 'menuLink',
    panelId: 'megaMenu',
    closeId: 'megamenux',
    closeOnOutsideClick: true,
    hoverToOpen: true,
    show: (panel) => panel.classList.add('is-open'),
    hide: (panel) => panel.classList.remove('is-open'),
  });

  createDisclosure({
    triggerId: 'hamburger-click',
    panelId: 'mobilenav',
    closeId: 'closeBtn',
    closeOnOutsideClick: false,
    show: (panel) => {
      panel.style.display = 'flex';
      panel.style.left = '0';
      // Flag the open menu so the fixed sticky add-to-cart bar can be hidden
      // underneath it. Raising the menu's z-index isn't enough: the ATB lives
      // in a different stacking context (the product page's transformed sticky
      // columns), so the two z-indexes don't compare.
      document.body.classList.add('baa-menu-open');
    },
    hide: (panel) => {
      panel.style.left = '-100%';
      panel.style.display = 'none';
      document.body.classList.remove('baa-menu-open');
    },
  });
})();

/* Magen Method — per-bundle description.
   The three "by Magen" variants (Essentials / Elevated / Complete) share one
   product.description, so instead we render bundle-specific copy that swaps with
   the native variant picker. Keyed by bundle name (substring), not variant id,
   so it survives the variants being rebuilt. Magen-scoped; every other product
   keeps its normal single description. The shared .product__description is
   hidden on this product in CSS and we own a [data-baa-variant-desc] node in
   its place. */
(() => {
  const SCOPE = '.product-the-magen-method';
  const scope = document.querySelector(SCOPE);
  if (!scope) return;

  const DESC = {
    essentials: `
      <p>Magen&rsquo;s streamlined routine for anyone who wants to keep skincare simple without sacrificing the fundamentals.</p>
      <p>Essentials focuses on the things we believe every great routine should do well: cleanse thoroughly, exfoliate effectively, refine the appearance of pores and texture, and protect the skin every single day.</p>
      <p>It&rsquo;s an approachable introduction to the Magen Method and a great place to start if you&rsquo;re new to medical-grade skincare or prefer a more simple routine.</p>`,
    elevated: `
      <p>Elevated includes everything we love about the foundational routine, with the addition of some of our favorite products for supporting smoother, brighter, healthier-looking skin as it ages.</p>
      <p>Peptides, antioxidants and growth factor technology make this protocol especially beautiful for anyone ready to invest more intentionally in prevention and visible skin quality.</p>`,
    complete: `
      <p>The full Magen Method. This is for the girl who wants the whole routine.</p>
      <p>Complete brings together Magen&rsquo;s most comprehensive collection of products for the face, eyes and neck, with targeted products for cleansing, exfoliation, hydration, brightening, texture, pores, visible signs of aging and daily protection.</p>
      <p>It&rsquo;s not a minimalist routine, and it isn&rsquo;t meant to be. This is the most advanced version of the Magen Method for someone who loves skincare and wants a comprehensive, results-driven protocol.</p>`,
  };

  const descFor = (value) => {
    const v = (value || '').toLowerCase();
    if (v.includes('complete')) return DESC.complete;
    if (v.includes('elevated')) return DESC.elevated;
    if (v.includes('essential')) return DESC.essentials;
    return null;
  };

  /* The currently-chosen bundle: the checked radio in the button picker (or the
     dropdown fallback). Its value is the option value, e.g. "Essentials by Magen". */
  const selectedValue = () => {
    const radio = scope.querySelector(
      'variant-radios input:checked, .product-form__input input[type="radio"]:checked'
    );
    if (radio) return radio.value;
    const select = scope.querySelector('variant-selects select, .product-form__input select');
    return select ? select.value : null;
  };

  /* Our description node — reuse Sense's .product__description styling. Sits
     directly BELOW the variant selector (so it reads as copy about the chosen
     bundle), styled as a cream box in CSS. Falls back to just after the shared
     intro description if the picker isn't found. */
  const host = (() => {
    const existing = scope.querySelector('[data-baa-variant-desc]');
    if (existing) return existing;
    const el = document.createElement('div');
    el.className = 'product__description rte';
    el.setAttribute('data-baa-variant-desc', '');
    const picker = scope.querySelector('variant-radios, variant-selects');
    const pickerWrap = (picker && picker.closest('.product-form__input')) || picker;
    if (pickerWrap && pickerWrap.parentNode) {
      pickerWrap.parentNode.insertBefore(el, pickerWrap.nextSibling);
      return el;
    }
    const nativeDesc = scope.querySelector('.product__description:not([data-baa-variant-desc])');
    if (nativeDesc && nativeDesc.parentNode) {
      nativeDesc.parentNode.insertBefore(el, nativeDesc.nextSibling);
      return el;
    }
    return null;
  })();
  if (!host) return;

  const apply = () => {
    const html = descFor(selectedValue());
    if (html != null) host.innerHTML = html;
  };

  apply();
  /* The picker fires a native change (radios) that bubbles to the scope before
     Sense re-renders price/media, so read straight off it — no dependency on the
     section-rendering fetch. */
  scope.addEventListener('change', (e) => {
    if (e.target.matches('input[type="radio"], select')) apply();
  });
})();

/* Xtresse Hair Growth Gummies — per-bag price framing on the Appstle
   volume-discount (subscription tier) widget. The app renders only each tier's
   TOTAL (e.g. $421.86 for the 6-month prepaid supply); Julia wants the full
   one-time price ($89.00) struck through and the per-bag price shown instead.
   No app setting exposes this, so we compute per-bag = total / months and inject
   our own price node, hiding the app's native total via CSS (:has(.baa-perbag)).
   One-Time is left untouched — it IS the $89 full price. Appstle's React widget
   re-renders on tier select, so a debounced MutationObserver re-applies. Scoped
   to this product only; keys off stable ab-volume-discount-item-* classes so it
   never touches other products' widgets. */
(() => {
  const SCOPE = '.product-xtresse-hair-growth-gummies';
  const scope = document.querySelector(SCOPE);
  if (!scope) return;

  const CARD = '.ab-volume-discount-item-content';
  const TITLE = '.ab-volume-discount-item-title-label';
  const PRICE = '.ab-volume-discount-item-discount-price';
  const INFO = '.ab-volume-discount-item-price-info';

  /* "$421.86" -> 421.86 (null if there's no number). */
  const money = (text) => {
    const m = (text || '').replace(/,/g, '').match(/\$?\s*([\d.]+)/);
    return m ? parseFloat(m[1]) : null;
  };
  const fmt = (n) => '$' + n.toFixed(2);

  /* Months (= bags) in a tier, from its title: "Monthly Delivery" -> 1,
     "6-Month Delivery" -> 6. One-Time and anything unparseable -> null, so the
     card is skipped and keeps the app's native price. */
  const monthsOf = (title) => {
    const t = (title || '').toLowerCase();
    if (t.includes('one-time') || t.includes('one time')) return null;
    const m = t.match(/(\d+)\s*-?\s*month/);
    if (m) return parseInt(m[1], 10);
    if (t.includes('monthly')) return 1;
    return null;
  };

  /* The full price = the One-Time tier's price, read live so a price change
     never needs a code edit. Null if the tier can't be found -> we bail. */
  const fullPrice = () => {
    for (const card of scope.querySelectorAll(CARD)) {
      const title = card.querySelector(TITLE);
      if (title && /one-?time/i.test(title.textContent)) {
        return money(card.querySelector(PRICE)?.textContent);
      }
    }
    return null;
  };

  const apply = () => {
    const full = fullPrice();
    if (full == null) return;
    scope.querySelectorAll(CARD).forEach((card) => {
      const months = monthsOf(card.querySelector(TITLE)?.textContent);
      if (months == null || months <= 0) return; // One-Time: leave native $89
      const priceEl = card.querySelector(PRICE);
      const info = card.querySelector(INFO);
      if (!priceEl || !info) return;
      const total = money(priceEl.textContent);
      if (total == null) return;
      const perBag = total / months;

      /* Reuse our node when the numbers are unchanged. React re-renders the
         card, so re-inject if our node was wiped or the price moved; the stamp
         check also stops the observer from looping on our own mutations. */
      let node = info.querySelector('.baa-perbag');
      const stamp = full.toFixed(2) + '|' + perBag.toFixed(2);
      if (node && node.dataset.stamp === stamp) return;
      if (!node) {
        node = document.createElement('div');
        node.className = 'baa-perbag';
        info.appendChild(node);
      }
      node.dataset.stamp = stamp;
      node.innerHTML =
        '<span class="baa-perbag__was">' + fmt(full) + '</span>' +
        '<span class="baa-perbag__now">' + fmt(perBag) + '</span>' +
        '<span class="baa-perbag__unit">/bag</span>';
    });
  };

  apply();
  let queued = false;
  const observer = new MutationObserver(() => {
    if (queued) return;
    queued = true;
    setTimeout(() => { queued = false; apply(); }, 0);
  });
  observer.observe(scope, { childList: true, subtree: true, characterData: true });
})();
